import { queryOptions } from '@tanstack/react-query';

import { ApiError } from './errors';
import type { GraphUserDto, PhotoUploadAckDto } from './generated';
import {
  usersControllerDeleteUserPhoto,
  usersControllerGetUser,
  usersControllerGetUserPhoto,
  usersControllerSearchUsers,
  usersControllerUploadUserPhoto,
} from './generated';

export type GraphUser = GraphUserDto;

/**
 * The two answers a photo upload can give. Which one arrives is an
 * authorisation fact: without `moderate:Photo` the server files the photo for
 * moderation instead of sending it to Entra, and the card says so.
 */
export type PhotoUploadAck = PhotoUploadAckDto;

export const usersSearchQueryOptions = (q: string) =>
  queryOptions({
    queryKey: ['users', 'search', q] as const,
    // `signal`: React Query aborts the request once its key is superseded
    // (a newer search) or no component reads it any more.
    queryFn: async ({ signal }) => {
      const { data } = await usersControllerSearchUsers({
        query: { q },
        signal,
        throwOnError: true,
      });
      return data;
    },
    enabled: q.length >= 2,
    staleTime: 30_000,
  });

export const userQueryOptions = (oid: string) =>
  queryOptions({
    queryKey: ['users', oid] as const,
    queryFn: async () => {
      const { data } = await usersControllerGetUser({
        path: { userId: oid },
        throwOnError: true,
      });
      return data;
    },
    staleTime: 60_000,
  });

// Prefix key for a user's photo. Invalidating this (via invalidateQueries)
// matches the key below, so all avatars for the user refetch when their Entra
// photo is updated/deleted.
export const userPhotoQueryKey = (oid: string) =>
  ['users', oid, 'photo'] as const;

// Returns the photo as a Blob (cached) rather than an object URL, so consumers
// own the URL.createObjectURL / revoke lifecycle per-mount. A missing photo
// (404) is a normal state → null, and we don't retry it. Caching here is what
// stops each search result from re-fetching its avatar (2 Graph calls each).
// Forcing a refetch is `invalidateQueries` on the key above, not an argument.
export const userPhotoQueryOptions = (oid: string) =>
  queryOptions({
    queryKey: userPhotoQueryKey(oid),
    queryFn: async (): Promise<Blob | null> => {
      try {
        const { data } = await usersControllerGetUserPhoto({
          path: { userId: oid },
          throwOnError: true,
        });
        return data;
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) {
          return null;
        }
        throw err;
      }
    },
    staleTime: 5 * 60_000,
    gcTime: 10 * 60_000,
    retry: false,
  });

export async function uploadUserPhoto(
  oid: string,
  file: Blob,
  params: { targetUpn: string; targetName: string },
): Promise<PhotoUploadAck> {
  const { data } = await usersControllerUploadUserPhoto({
    path: { userId: oid },
    query: params,
    body: { photo: file },
    throwOnError: true,
  });
  return data;
}

export async function deleteUserPhoto(
  oid: string,
  params: { targetUpn: string; targetName: string },
): Promise<void> {
  await usersControllerDeleteUserPhoto({
    path: { userId: oid },
    query: params,
    throwOnError: true,
  });
}
